/**
 * useWidgetFrameProps — [T149-PR8-CONT-11] state composition hook.
 *
 * The public `WidgetFrame` is a thin slot composer. Every per-slot prop
 * bundle is derived ONCE in this hook, then forwarded to the matching
 * `Chrome / HeaderSlot / BodySlot / FooterSlot`. Centralising the
 * composition in a single hook guarantees that all 4 slots observe
 * the same state snapshot (no stale-closure footguns).
 *
 * Pure derivation — no effects, no side-effects, no state ownership.
 * `pendingRemove` lives in the public component (it's the only piece of
 * state with view-mode confirmation flow).
 *
 * Post-[T-DASHBOARD-VISIBLE-HEADER] (v4): `icon` and `showTitle` are now
 * surfaced into `HeaderState` so the visible header row can render the
 * icon + title + actions slot. `iconBg` and `actions` remain on the
 * public `FrameProps` for backwards compat but are not surfaced (legacy
 * icon-circle background + actions-slot pattern is superseded by the
 * new context-driven actions).
 *
 * @see https://react.dev/reference/react/useMemo — memoised derivation
 */

import { useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type {
  BodyState,
  ChromeState,
  FooterState,
  FrameProps,
  HeaderState,
} from './types';

export interface ComposedFrameState {
  chrome: ChromeState;
  header: HeaderState;
  body: BodyState;
  footer: FooterState;
  /** Pre-derived `${id}-title` for the `aria-labelledby` chain. */
  headerId: string;
  /** `formatDistanceToNow(lastUpdatedAt, { addSuffix: true })` or null. */
  lastUpdatedLabel: string | null;
}

/**
 * Apply defaults + derive per-slot state from the public `FrameProps`.
 *
 * Defaults MUST match the pre-refactor monolithic component:
 *   - `noGlass = true`
 *   - `isRefreshing = false`
 *   - `isNoData = false`
 *   - `isTimeout = false`
 *   - `isViewMode = false`
 *
 * `icon` and `showTitle` are surfaced into `HeaderState` so `HeaderSlot`
 * can render the visible 36px header row (v4 design).
 *
 * NB: `iconBg` and `actions` are still part of `FrameProps` (public API)
 * but are NOT surfaced into `HeaderState` — the legacy icon-circle
 * background and actions-slot pattern are superseded by the new
 * context-driven actions (WidgetHeaderActions reads from WidgetContext).
 */
export function useWidgetFrameProps(props: FrameProps): ComposedFrameState {
  const {
    id,
    title,
    icon,
    showTitle,
    body,
    footer,
    className,
    noGlass = false,
    ariaProps,
    isRefreshing = false,
    lastUpdatedAt,
    errorMessage,
    compatError,
    onRetry,
    onRequestFix,
    compatibilityWarnings,
    isNoData = false,
    noDataMessage,
    isTimeout = false,
    isViewMode = false,
    onRequestViewModeFix,
    onRequestRemove,
    // NB: `iconBg` and `actions` remain on `FrameProps` (public API) but
    // are NOT surfaced into `HeaderState` — the legacy icon-circle
    // background and actions-slot pattern are superseded by the new
    // context-driven actions (WidgetHeaderActions reads from WidgetContext).
  } = props;

  const headerId = `${id}-title`;

  const lastUpdatedLabel = lastUpdatedAt
    ? formatDistanceToNow(lastUpdatedAt, { addSuffix: true })
    : null;

  return useMemo<ComposedFrameState>(
    () => ({
      headerId,
      lastUpdatedLabel,
      chrome: {
        id,
        className,
        noGlass,
        ariaProps,
      },
      header: {
        id,
        headerId,
        title,
        icon,
        showTitle,
      },
      body: {
        id,
        title,
        body,
        isRefreshing,
        compatibilityWarnings,
        errorMessage,
        compatError,
        onRetry,
        onRequestFix,
        isNoData,
        noDataMessage,
        isTimeout,
        isViewMode,
        onRequestViewModeFix,
        onRequestRemove,
      },
      footer: {
        id,
        footer,
        lastUpdatedLabel,
      },
    }),
    [
      id,
      title,
      icon,
      showTitle,
      body,
      footer,
      className,
      noGlass,
      ariaProps,
      isRefreshing,
      lastUpdatedAt,
      errorMessage,
      compatError,
      onRetry,
      onRequestFix,
      compatibilityWarnings,
      isNoData,
      noDataMessage,
      isTimeout,
      isViewMode,
      onRequestViewModeFix,
      onRequestRemove,
      headerId,
      lastUpdatedLabel,
    ],
  );
}
